import React, { useState, useEffect } from "react";
import { MdKeyboardArrowRight, MdKeyboardArrowDown } from "react-icons/md";
import { NavLink, useNavigate, useLocation, matchPath } from "react-router-dom";
import { useSelector } from "react-redux";

// Import your logo and icons
import logo from "../../assets/icons/Logo.png";
import manageTagIcon from "../../assets/icons/sidebar/manage-tag.svg";
import manageArtistIcon from "../../assets/icons/sidebar/manage-artist.svg";
import manageTracksIcon from "../../assets/icons/sidebar/manage-tracks.svg";
import managePlaylistIcon from "../../assets/icons/sidebar/manage-playlist.svg";
import remixerAccountIcon from "../../assets/icons/sidebar/remixer-account.svg";
import userMonitoringIcon from "../../assets/icons/sidebar/user-monitoring.svg";
import customersIcon from "../../assets/icons/sidebar/customers.svg";
import reportsIcon from "../../assets/icons/sidebar/reports.svg";
import couponCodesIcon from "../../assets/icons/sidebar/coupon-codes.svg";
import topEditsIcon from "../../assets/icons/sidebar/top-edits.svg";
import chartIcon from "../../assets/sidebar/bar-chart.png";
import vectorIcon from "../../assets/sidebar/Vector.svg";
import starIcon from "../../assets/sidebar/starIcon.png";
import puzzleIcon from "../../assets/sidebar/Vector.png";
import producerIcon from "../../assets/producer/Group.png";
import downloadTrackingIcon from "../../assets/icons/sidebar/download-tracking.svg";
import { roles } from "../../constants/commonConstants";
import { pageTitleMap } from "../../constants/pageTitles";

// SidebarLink component with role check
const SidebarLink = ({
  icon,
  label,
  onClick = () => {},
  isActive,
  path,
  requiredRole,
  currentRole,
}) => {
  const navigate = useNavigate();

  const handleClick = (e) => {
    if (
      requiredRole &&
      requiredRole !== "common" &&
      requiredRole !== currentRole
    ) {
      e.preventDefault();
      navigate("/unauthorized");
      return;
    }
    onClick(label, path);
  };

  return (
    <NavLink to={path} onClick={handleClick} end>
      <div
        className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer ${
          isActive ? "" : "hover:bg-[#0A1330]"
        }`}
      >
        <img
          src={icon}
          alt={`${label} icon`}
          className="w-5 h-5"
          style={{
            filter: isActive
              ? "brightness(0) saturate(100%) invert(75%) sepia(6%) saturate(7241%) hue-rotate(135deg) brightness(103%) contrast(101%)"
              : "brightness(0) saturate(100%) invert(65%) sepia(1%) saturate(121%) hue-rotate(191deg) brightness(92%) contrast(88%)",
          }}
        />
        <span
          className={`text-[15px] font-medium ${
            isActive
              ? "bg-[#4AE9EC] bg-clip-text text-transparent"
              : "text-[#AEB9E1]"
          }`}
        >
          {label}
        </span>
      </div>
    </NavLink>
  );
};

// SidebarDropdown component with role check
const SidebarDropdown = ({
  icon,
  label,
  isOpen,
  onToggle,
  onTitleClick,
  items = [],
  activeLabel,
  setActiveLabel,
  requiredRole,
  currentRole,
  currentPath,
}) => {
  const navigate = useNavigate();

  if (!Array.isArray(items) || !currentPath) return null;

  const handleTitleClick = () => {
    if (
      requiredRole &&
      requiredRole !== "common" &&
      requiredRole !== currentRole
    ) {
      navigate("/unauthorized");
      return;
    }
    onToggle?.();
    onTitleClick?.(label);
    setActiveLabel?.(label);
  };

  const handleItemClick = (item) => {
    if (
      requiredRole &&
      requiredRole !== "common" &&
      requiredRole !== currentRole
    ) {
      navigate("/unauthorized");
      return;
    }
    onTitleClick?.(item.name);
  };

  const pathMatch = (itemPath) => {
    try {
      return (
        itemPath &&
        currentPath &&
        matchPath({ path: itemPath, end: false }, currentPath)
      );
    } catch {
      return false;
    }
  };

  return (
    <div>
      <div
        className={`flex items-center justify-between px-4 py-2 rounded-lg cursor-pointer ${
          items.some((i) => pathMatch(i?.path)) || items.some((i) => activeLabel === i.name) ? "" : "hover:bg-[#0A1330]"
        }`}
        onClick={handleTitleClick}
      >
        <div className="flex items-center gap-2">
          <img
            src={icon}
            alt={`${label} icon`}
            className="w-5 h-5"
            style={{
              filter: items.some((i) => pathMatch(i?.path)) || items.some((i) => activeLabel === i.name)
                ? "brightness(0) saturate(100%) invert(75%) sepia(6%) saturate(7241%) hue-rotate(135deg) brightness(103%) contrast(101%)"
                : "brightness(0) saturate(100%) invert(65%) sepia(1%) saturate(121%) hue-rotate(191deg) brightness(92%) contrast(88%)",
            }}
          />
          <span
            className={`text-[15px] font-normal font-['Public_Sans'] ${
              items.some(
                (i) =>
                  i?.path &&
                  matchPath({ path: i.path, end: false }, currentPath),
              ) || items.some((i) => activeLabel === i.name)
                ? "bg-[#4AE9EC] bg-clip-text text-transparent"
                : "text-[#AEB9E1]"
            }`}
          >
            {label}
          </span>
        </div>
        <div className="text-[#AEB9E1] min-w-[20px] text-right">
          {isOpen ? <MdKeyboardArrowDown /> : <MdKeyboardArrowRight />}
        </div>
      </div>

      {isOpen && (
        <div className="ml-8 mt-1 flex flex-col gap-1">
          {items.map((item, idx) => {
            const isItemActive = pathMatch(item.path) || activeLabel === item.name;
            return (
              <NavLink
                to={item.path}
                key={idx}
                onClick={() => handleItemClick(item)}
              >
                <div
                  className={`text-sm py-1 px-2 rounded cursor-pointer ${
                    isItemActive ? "" : "hover:bg-[#081028]"
                  }`}
                >
                  <span
                    className={`text-[15px] font-normal font-['Public_Sans'] ${
                      isItemActive
                        ? "bg-[#4AE9EC] bg-clip-text text-transparent"
                        : "text-[#AEB9E1]"
                    }`}
                  >
                    {item.name}
                  </span>
                </div>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
};
// Define Admin sidebar items with role requirements
const adminSidebarItems = [
  {
    type: "dropdown",
    icon: manageTracksIcon,
    label: "Manage Tracks",
    path: "/",
    role: "common", // Both admin and remixer can access
    items: [
      { name: "Manage Track", path: "/manage-track" },
      { name: "Add New Tracks", path: "/add-tracks" },
    ],
  },
  {
    type: "dropdown",
    icon: managePlaylistIcon,
    label: "Manage Playlist",
    role: roles.ADMIN,
    items: [
      { name: "Manage Playlist", path: "/manage-playlist" },
      { name: "Add Subcategory", path: "/manage-playlist-subcategoryManager" },
    ],
  },
  {
    type: "link",
    icon: manageArtistIcon,
    label: "Manage Artist",
    path: "/manage-artist",
    role: roles.ADMIN,
  },
  {
    type: "link",
    icon: manageArtistIcon,
    label: "Manage Remixers",
    path: "/manage-remixers",
    role: roles.ADMIN,
  },
  {
    type: "link",
    icon: manageTagIcon,
    label: "Manage Tags",
    path: "/manage-tags",
    role: roles.ADMIN,
  },
  {
    type: "dropdown",
    icon: producerIcon,
    label: "Manage Producer",
    role: roles.ADMIN,
    items: [
      { name: "Manage Producer Pack", path: "/manage-producer-packs" },
      {
        name: "Manage Producer Category",
        path: "/producer-category-management",
      },
      { name: "Manage Producer Tag", path: "/producer-tag-management" },
    ],
  },
  {
    type: "link",
    icon: topEditsIcon,
    label: "Top 10 Edits",
    path: "/top-edits",
    role: roles.ADMIN,
  },
  {
    type: "link",
    icon: chartIcon,
    label: "Manage Charts",
    path: "/chart-management",
    role: roles.ADMIN,
  },
  {
    type: "dropdown",
    icon: customersIcon,
    label: "Customers",
    role: roles.ADMIN,
    items: [
      { name: "Customer Overview", path: "/manage-customer" },
      { name: "Pending Customer Requests", path: "/pending-requests" },
    ],
  },
  {
    type: "dropdown",
    icon: remixerAccountIcon,
    label: "Remixer Account",
    role: roles.ADMIN,
    items: [
      { name: "Remixer Overview", path: "/remixer-overview" },
      { name: "Add new Remixer profile", path: "/remixer-user/add" },
    ],
  },
  {
    type: "link",
    icon: userMonitoringIcon,
    label: "User Monitoring",
    path: "/user-monitoring",
    role: roles.ADMIN,
  },
  {
    type: "link",
    icon: downloadTrackingIcon,
    label: "Download Tracking",
    path: "/download-tracking",
    role: roles.ADMIN,
  },
  {
    type: "dropdown",
    icon: reportsIcon,
    label: "Reports",
    role: roles.ADMIN,
    items: [
      { name: "Subscription Report", path: "/subscription-report" },
      { name: "Download Report", path: "/download-report" },
    ],
  },
  {
    type: "dropdown",
    icon: couponCodesIcon,
    label: "Coupon Codes",
    role: roles.ADMIN,
    items: [
      { name: "One Time Coupons", path: "/onetime-coupon-codes" },
      { name: "Unlimited Coupons", path: "/unlimited-coupon-codes" },
      { name: "One Time Amount Coupons", path: "/onetime-amount-coupon-codes" },
      {
        name: "Unlimited Amount Coupons",
        path: "/unlimited-amount-coupon-codes",
      },
    ],
  },
  {
    type: "link",
    icon: puzzleIcon,
    label: "Manage Landing Coupon",
    path: "/landing-coupon-management",
    role: roles.ADMIN,
  },
  {
    type: "link",
    icon: vectorIcon,
    label: "Manage Banners",
    path: "/banner-management",
    role: roles.ADMIN,
  },
  {
    type: "link",
    icon: manageArtistIcon,
    label: "Consents log",
    path: "/cookie-consent-log",
    role: roles.ADMIN,
  },
  {
    type: "link",
    icon: puzzleIcon,
    label: "Breach Notification",
    path: "/breach-notification",
    role: roles.ADMIN,
  },
];

// Define Remixer sidebar items with role requirements
const remixerSidebarItems = [
  {
    type: "dropdown",
    icon: manageTracksIcon,
    label: "Manage Tracks",
    path: "/",
    role: "common", // Both admin and remixer can access
    items: [
      { name: "Manage Track", path: "/remixer-manage-track" },
      { name: "Add New Tracks", path: "/remixer-add-tracks" },
    ],
  },
  {
    type: "link",
    icon: manageArtistIcon,
    label: "Manage Artists",
    path: "/remixer-artists",
    role: roles.REMIXER,
  },
  {
    type: "link",
    icon: manageTagIcon,
    label: "Manage Tags",
    path: "/remixer-manage-tags",
    role: roles.REMIXER,
  },
  {
    type: "link",
    icon: remixerAccountIcon,
    label: "Profile",
    path: "/profile",
    role: roles.REMIXER,
  },
];

// Admin Sidebar Component
const AdminSidebar = ({ setTitle }) => {
  const [openDropdowns, setOpenDropdowns] = useState([]);
  const { role } = useSelector((state) => state.auth);
  const location = useLocation();
  const currentPath = location?.pathname || "/";

  const getInitialActiveLabel = () => {
    for (const path in pageTitleMap) {
      if (matchPath({ path, end: true }, currentPath)) {
        return pageTitleMap[path];
      }
    }

    for (const item of adminSidebarItems) {
      if (
        item.type === "link" &&
        matchPath({ path: item.path, end: true }, currentPath)
      ) {
        return item.label;
      }
      if (item.type === "dropdown") {
        const activeChild = item.items.find((sub) =>
          matchPath({ path: sub.path, end: true }, currentPath),
        );
        if (activeChild) return activeChild.name;
      }
    }

    return "";
  };

  const [activeLabel, setActiveLabel] = useState(getInitialActiveLabel());

  useEffect(() => {
    const manualMatch = [
      {
        match: (path) => path === "/" || path === "",
        dropdown: "Manage Tracks",
        title: "Manage Track",
        activeItem: "Manage Track",
      },
      {
        match: (path) =>
          matchPath(
            { path: "/manage-playlist-add-new-playlist", end: true },
            path,
          ),
        dropdown: "Manage Playlist",
        title: "Add New Playlist",
      },
      {
        match: (path) =>
          matchPath({ path: "/edit-playlist/:id", end: false }, path),
        dropdown: "Manage Playlist",
        title: "Edit Playlist",
      },
      {
        match: (path) =>
          matchPath({ path: "/edit-track/:id", end: false }, path),
        dropdown: "Manage Tracks",
        title: "Edit Track",
      },
      {
        match: (path) => path.startsWith("/customer/:id"),
        dropdown: "Customers",
      },
      {
        match: (path) =>
          matchPath({ path: "/remixer-user/view/:id", end: false }, path),
        dropdown: "Remixer Account",
        title: null, // Don't set title, let the component handle it
      },
    ];

    for (const { match, dropdown, title, activeItem } of manualMatch) {
      if (match(currentPath)) {
        setOpenDropdowns([dropdown]);
        setActiveLabel(activeItem || dropdown);
        if (title) {
          setTitle(title);
        }
        return;
      }
    }

    const label = getInitialActiveLabel();
    setActiveLabel(label);

    // Only set title if it's not a dynamic route
    if (
      !matchPath({ path: "/remixer-user/view/:id", end: false }, currentPath)
    ) {
      setTitle(label);
    }

    const dropdown = adminSidebarItems.find(
      (item) =>
        item.type === "dropdown" &&
        item.items.some((sub) =>
          matchPath({ path: sub.path, end: false }, currentPath),
        ),
    );

    setOpenDropdowns(dropdown ? [dropdown.label] : []);
    if (dropdown) setActiveLabel(dropdown.label);
  }, [currentPath]);

  const toggleDropdown = (label) => {
    setOpenDropdowns((prev) => (prev.includes(label) ? [] : [label]));
  };

  const isActive = (path) =>
    matchPath({ path, end: true }, currentPath) !== null;

  const handleClick = (label, path) => {
    const formalTitle = pageTitleMap[path] || label;
    setTitle(formalTitle);
    setActiveLabel(label);
  };

  return (
    <aside className="fixed top-0 left-0 w-[268px] md:w-[288px] lg:w-[298px] xl:w-[300px] border-r border-[#1A1A1A] h-screen bg-[#080B12] shadow-[0px_8px_28px_rgba(1,5,17,0.3)] text-[#AEB9E1] font-sans p-4 flex flex-col justify-between transition-transform duration-300">
      <div className="flex flex-col px-0 pt-0 h-full">
        <div className="px-6 mb-6 shrink-0">
          <img src="/Supremelogo.svg" alt="Supreme Edits" className="h-10" />
        </div>

        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar-stable">
          <div className="flex flex-col gap-2">
            {adminSidebarItems.map((item, idx) =>
              item.type === "link" ? (
                <SidebarLink
                  key={idx}
                  icon={item.icon}
                  label={item.label}
                  path={item.path}
                  onClick={handleClick}
                  isActive={isActive(item.path)}
                  requiredRole={item.role}
                  currentRole={role}
                />
              ) : (
                <SidebarDropdown
                  key={idx}
                  icon={item.icon}
                  label={item.label}
                  isOpen={openDropdowns.includes(item.label)}
                  onToggle={() => toggleDropdown(item.label)}
                  items={item.items}
                  activeLabel={activeLabel}
                  setActiveLabel={setActiveLabel}
                  currentPath={currentPath}
                  requiredRole={item.role}
                  currentRole={role}
                />
              ),
            )}
          </div>
        </div>

        <div className="text-center text-[8px] text-[#979798] mt-10 shrink-0">
          © 2026 Supreme Edits. All rights reserved.
        </div>
      </div>
    </aside>
  );
};

// Remixer Sidebar Component
const RemixerSidebar = ({ setTitle }) => {
  const [openDropdowns, setOpenDropdowns] = useState([]);
  const { role } = useSelector((state) => state.auth);
  const location = useLocation();
  const currentPath = location?.pathname || "/remixer-tags";

  const getInitialActiveLabel = () => {
    for (const path in pageTitleMap) {
      if (matchPath({ path, end: true }, currentPath)) {
        return pageTitleMap[path];
      }
    }

    for (const item of remixerSidebarItems) {
      if (
        item.type === "link" &&
        matchPath({ path: item.path, end: true }, currentPath)
      ) {
        return item.label;
      }
      if (item.type === "dropdown") {
        const activeChild = item.items.find((sub) =>
          matchPath({ path: sub.path, end: true }, currentPath),
        );
        if (activeChild) return activeChild.name;
      }
    }

    return "";
  };

  const [activeLabel, setActiveLabel] = useState(getInitialActiveLabel());

  useEffect(() => {
    const manualMatch = [
      {
        match: (path) => path === "/" || path === "",
        dropdown: "Manage Tracks",
        title: "Manage Track",
        activeItem: "Manage Track",
      },
      {
        match: (path) =>
          matchPath(
            { path: "/manage-playlist-add-new-playlist", end: true },
            path,
          ),
        dropdown: "Manage Playlist",
        title: "Add New Playlist",
      },
      {
        match: (path) =>
          matchPath({ path: "/edit-playlist/:id", end: false }, path),
        dropdown: "Manage Playlist",
        title: "Edit Playlist",
      },
      {
        match: (path) =>
          matchPath({ path: "/edit-track/:id", end: false }, path),
        dropdown: "Manage Tracks",
        title: "Edit Track",
      },
      {
        match: (path) => path.startsWith("/customer/:id"),
        dropdown: "Customers",
      },
      {
        match: (path) =>
          matchPath({ path: "/remixer-user/view/:id", end: false }, path),
        dropdown: "Remixer Account",
        title: null,
      },
    ];

    for (const { match, dropdown, title, activeItem } of manualMatch) {
      if (match(currentPath)) {
        setOpenDropdowns([dropdown]);
        setActiveLabel(activeItem || dropdown);
        if (title) {
          setTitle(title);
        }
        return;
      }
    }

    const label = getInitialActiveLabel();
    setActiveLabel(label);

    // Only set title if it's not a dynamic route
    if (
      !matchPath({ path: "/remixer-user/view/:id", end: false }, currentPath)
    ) {
      setTitle(label);
    }

    const dropdown = remixerSidebarItems.find(
      (item) =>
        item.type === "dropdown" &&
        item.items.some((sub) =>
          matchPath({ path: sub.path, end: false }, currentPath),
        ),
    );

    setOpenDropdowns(dropdown ? [dropdown.label] : []);
    if (dropdown) setActiveLabel(dropdown.label);
  }, [currentPath]);

  const toggleDropdown = (label) => {
    setOpenDropdowns((prev) => (prev.includes(label) ? [] : [label]));
  };

  const isActive = (path) =>
    matchPath({ path, end: true }, currentPath) !== null;

  const handleClick = (label, path) => {
    const formalTitle = pageTitleMap[path] || label;
    setTitle(formalTitle);
    setActiveLabel(label);
  };

  return (
    <aside className="fixed top-0 left-0 w-[268px] md:w-[288px] lg:w-[298px] xl:w-[300px] border-r border-[#1A1A1A] h-screen bg-[#080B12] shadow-[0px_8px_28px_rgba(1,5,17,0.3)] text-[#AEB9E1] font-sans p-4 flex flex-col justify-between transition-transform duration-300">
      <div className="flex flex-col px-0 pt-0 h-full">
        <div className="px-6 mb-6 shrink-0">
          <img src="/Supremelogo.svg" alt="Supreme Edits" className="h-10" />
        </div>

        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar-stable">
          <div className="flex flex-col gap-2">
            {remixerSidebarItems.map((item, idx) =>
              item.type === "link" ? (
                <SidebarLink
                  key={idx}
                  icon={item.icon}
                  label={item.label}
                  path={item.path}
                  onClick={handleClick}
                  isActive={isActive(item.path)}
                  requiredRole={item.role}
                  currentRole={role}
                />
              ) : (
                <SidebarDropdown
                  key={idx}
                  icon={item.icon}
                  label={item.label}
                  isOpen={openDropdowns.includes(item.label)}
                  onToggle={() => toggleDropdown(item.label)}
                  items={item.items}
                  activeLabel={activeLabel}
                  setActiveLabel={setActiveLabel}
                  currentPath={currentPath}
                  requiredRole={item.role}
                  currentRole={role}
                />
              ),
            )}
          </div>
        </div>

        <div className="text-center text-[8px] text-[#979798] mt-10 shrink-0">
          © 2026 Supreme Edits. All rights reserved.
        </div>
      </div>
    </aside>
  );
};

// Main Sidebar component that conditionally renders based on user role
const SideNavbar = ({ setTitle }) => {
  const { role } = useSelector((state) => state.auth);

  if (!role || typeof role !== "string") return null;

  // Return appropriate sidebar based on role
  return role === roles.ADMIN ? (
    <AdminSidebar setTitle={setTitle} />
  ) : (
    <RemixerSidebar setTitle={setTitle} />
  );
};

export default SideNavbar;
